import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { formatINR } from '@/lib/api';
import { Storefront, MapPin, Plus, Minus } from '@phosphor-icons/react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export default function StorePage() {
  const { slug } = useParams();
  const { add, qtyOf, increment, decrement } = useCart();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get(`/sellers/public/${slug}`),
          api.get(`/sellers/public/${slug}/products`),
        ]);
        setStore(s.data);
        setProducts(p.data || []);
      } catch (e) {
        toast.error('Store not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-stone-500">Loading store…</div>;
  if (!store) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-stone-500">Store not found.</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="store-page">
      {/* Store header */}
      <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden">
        <div className="h-36 sm:h-48 bg-gradient-to-r from-cream-100 via-olive-100 to-terracotta-100 relative">
          {store.banner_url && <img src={store.banner_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="p-5 sm:p-7 flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 -mt-10 sm:-mt-14">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white border-4 border-white shadow-md grid place-items-center text-terracotta-600 overflow-hidden">
            {store.logo_url ? <img src={store.logo_url} alt="" className="w-full h-full object-cover"/> : <Storefront size={44} weight="duotone"/>}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 data-testid="store-name" className="font-heading text-3xl text-stone-900">{store.shop_name}</h1>
              {store.is_open
                ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-olive-100 text-olive-800">OPEN</span>
                : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-200 text-stone-700">CLOSED</span>}
            </div>
            <p className="text-sm text-stone-600 mt-1 flex items-center gap-1"><MapPin size={14}/>{store.address || 'Faridabad'} · PIN {store.pincode}</p>
            {store.description && <p className="text-stone-700 mt-2 max-w-2xl">{store.description}</p>}
          </div>
          <button
            data-testid="store-share-btn"
            onClick={() => {
              const url = `${window.location.origin}/marketplace/store/${store.slug}`;
              const text = `Shop at ${store.shop_name} on MyZippyKart: ${url}`;
              if (navigator.share) navigator.share({ title: store.shop_name, url, text }).catch(() => {});
              else { navigator.clipboard.writeText(url); toast.success('Store link copied'); }
            }}
            className="h-10 px-4 rounded-full border border-stone-300 text-sm text-stone-700 hover:bg-stone-50"
          >
            Share store
          </button>
        </div>
      </div>

      {/* Products */}
      <h2 className="font-heading text-2xl text-stone-900 mt-10 mb-4">Products</h2>
      {products.length === 0 ? (
        <div className="py-10 text-center bg-white border border-dashed border-stone-200 rounded-2xl text-stone-500">This store hasn't added products yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="store-product-grid">
          {products.map((p) => {
            const inCart = qtyOf(p.id);
            const outOfStock = p.stock_status !== 'in_stock' || !store.is_open;
            return (
              <div key={p.id} data-testid={`vendor-product-${p.id}`} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <div className="aspect-square bg-cream-100">
                  {p.image_url
                    ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full grid place-items-center text-stone-300"><Storefront size={36}/></div>}
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-stone-900 line-clamp-2 min-h-[2.5rem]">{p.name}</p>
                  <p className="text-[11px] text-stone-500 mt-1">{p.unit}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      <span className="font-semibold text-stone-900">{formatINR(p.price)}</span>
                      {p.mrp > p.price && <span className="text-[11px] text-stone-400 line-through ml-1">{formatINR(p.mrp)}</span>}
                    </div>
                    {outOfStock ? (
                      <span className="text-[10px] text-stone-500 px-2 py-1 rounded bg-stone-100">Unavailable</span>
                    ) : inCart ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => decrement(p.id)} className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 grid place-items-center"><Minus size={12}/></button>
                        <span className="text-sm font-semibold w-5 text-center">{inCart}</span>
                        <button onClick={() => increment(p.id)} className="w-7 h-7 rounded-full bg-terracotta-600 text-white hover:bg-terracotta-700 grid place-items-center"><Plus size={12}/></button>
                      </div>
                    ) : (
                      <button
                        data-testid={`add-vendor-product-${p.id}`}
                        onClick={() => add({ ...p, vendor_name: store.shop_name, vendor_slug: store.slug })}
                        className="h-8 px-3 rounded-full bg-terracotta-600 text-white text-xs font-semibold hover:bg-terracotta-700"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
